import React, { useState, useEffect, useCallback, useRef } from "react";
import { Navigate } from "react-router-dom";
import { Button, Modal, Form } from "react-bootstrap";
import { Toaster, toast } from "react-hot-toast";
import { Icon } from "@iconify/react";
import Select from "react-select";
import { createErpSocket } from "../utils/socketIo";
import { useButtonTableStyle } from "../utils/useButtonTableStyle";
import { formatDateToDDMMYYYY } from "./const";
import {
  listShopExpenses,
  createShopExpense,
  updateShopExpense,
  deleteShopExpense,
  listBankAccounts,
  isErpApiSuccess,
  getErpRequestErrorMessage,
  generateIdempotencyKey,
} from "../erpClient";
import {
  PAYMENT_METHOD_LABELS,
  parsePaymentModeFromApi,
} from "../utils/paymentLinesErpAdapter";
import { getSessionBranchId } from "../utils/sessionBranch";
import { hasPermission, PERMISSIONS } from "../utils/permissions";
import useIsCurrentFinancialYear, {
  FY_ADD_DISABLED_TITLE,
} from "../hooks/useIsCurrentFinancialYear";
import Pagination from "../components/Pagination";
import { useVisitedPageCache, visitedPageKey } from "../utils/visitedPageCache";

/** API expense_type codes (server-normalized) */
export const SHOP_EXPENSE_TYPES = [
  { value: "LIGHTBILL", label: "Light bill" },
  { value: "EMI", label: "EMI" },
  { value: "RENT", label: "Rent" },
  { value: "SALARY", label: "Salary" },
  { value: "OTHER", label: "Other" },
];

const LAST_BANK_STORAGE_KEY = "shopExpense_lastBankAccountId";

const SHOP_EXPENSE_PAYMENT_OPTIONS = [
  { value: "cash", label: "Cash" },
  { value: "upi", label: "UPI" },
  { value: "bank_transfer", label: "Bank Transfer" },
  { value: "cheque", label: "Cheque" },
  { value: "card", label: "Card" },
];

function needsBankAccount(payMode) {
  return String(payMode || "").toLowerCase().trim() !== "cash";
}

/** Map API row → UI pay_method key (cash | upi | bank_transfer | …). */
function parseExpensePayMode(raw) {
  const pm = String(raw?.payment_mode || "").toUpperCase();
  if (pm === "CASH") return "cash";
  if (raw?.pay_method) {
    return parsePaymentModeFromApi(raw.pay_method);
  }
  if (pm === "BANK") return "bank_transfer";
  return raw?.bank_account_id != null ? "bank_transfer" : "cash";
}

/** UI pay_method key → API pay_method (when payment_mode is BANK). */
function payMethodToApi(payMode) {
  const key = String(payMode || "bank_transfer").toLowerCase().trim();
  if (key === "bank_transfer") return "BANK_TRANSFER";
  return key.toUpperCase();
}

function buildShopExpensePaymentPayload(formPayMode, formBank, branchId) {
  const isCash = String(formPayMode || "").toLowerCase() === "cash";
  const payload = {
    payment_mode: isCash ? "CASH" : "BANK",
    ...(branchId ? { branch_id: branchId } : {}),
  };
  if (!isCash) {
    payload.pay_method = payMethodToApi(formPayMode);
    if (formBank?.value != null) {
      payload.bank_account_id = formBank.value;
    }
  }
  return payload;
}

function labelForPayMode(payMode) {
  const key = String(payMode || "").toLowerCase().trim();
  return PAYMENT_METHOD_LABELS[key] || payMode || "—";
}

function isMoneyBankAccount(bank) {
  const t = String(bank?.account_type ?? "")
    .toLowerCase()
    .trim();
  return t !== "metal";
}

function mapBankToOption(bank) {
  const bankName = String(bank?.bank_name || "").trim() || `Account #${bank?.id}`;
  const holder = String(bank?.holder_name || "").trim();
  return {
    value: Number(bank.id),
    label: holder ? `${bankName} (${holder})` : bankName,
    data: bank,
  };
}

function formatPaymentSource(row) {
  const payMode = parseExpensePayMode(row);
  const typeLabel = labelForPayMode(payMode);
  if (payMode === "cash") return "Cash";
  if (row?.bank_name) return `${typeLabel} · ${row.bank_name}`;
  if (row?.bank_account_id != null) {
    return `${typeLabel} · Bank #${row.bank_account_id}`;
  }
  return typeLabel;
}

const labelForType = (value) => {
  const u = String(value || "").toUpperCase();
  return SHOP_EXPENSE_TYPES.find((t) => t.value === u)?.label || value || "—";
};

/** Same subtle badge pattern as other tables (e.g. MetalGroupTable, SupplierTable); avoid `text-primary` — vintage theme forces it to theme maroon. */
const badgeClassForExpenseType = (expenseType) => {
  const u = String(expenseType || "").toUpperCase();
  switch (u) {
    case "LIGHTBILL":
      return "bg-info-subtle text-info";
    case "EMI":
      return "bg-warning-subtle text-warning";
    case "RENT":
      return "bg-secondary-subtle text-secondary";
    case "SALARY":
      return "bg-success-subtle text-success";
    case "OTHER":
      return "bg-light text-dark";
    default:
      return "bg-secondary-subtle text-secondary";
  }
};

function normalizeListPayload(res) {
  const d = res?.data;
  const inner = d?.data;
  const rows = Array.isArray(inner)
    ? inner
    : Array.isArray(d?.expenses)
      ? d.expenses
      : Array.isArray(d)
        ? d
        : [];
  const p = d?.pagination || {};
  const page = Number(p.page) || 1;
  const limit = Number(p.limit) || 10;
  const total = Number(p.total) ?? rows.length;
  const total_pages =
    Number(p.total_pages) || Math.max(1, Math.ceil(total / limit) || 1);
  return { rows, pagination: { page, limit, total, total_pages } };
}

function mapExpenseRow(raw) {
  return {
    id: raw.id,
    tenant_id: raw.tenant_id,
    branch_id: raw.branch_id,
    user_id: raw.user_id,
    expense_type: String(raw.expense_type || "").toUpperCase(),
    amount: parseFloat(raw.amount) || 0,
    expense_date: raw.expense_date
      ? String(raw.expense_date).split("T")[0]
      : "",
    comment: raw.comment ?? null,
    payment_mode: raw.payment_mode
      ? String(raw.payment_mode).toUpperCase()
      : "BANK",
    pay_method: raw.pay_method
      ? String(raw.pay_method).toUpperCase()
      : null,
    bank_account_id:
      raw.bank_account_id != null ? Number(raw.bank_account_id) : null,
    bank_name: raw.bank_name ?? null,
    document_id: raw.document_id != null ? Number(raw.document_id) : null,
    ledger_transaction_id:
      raw.ledger_transaction_id != null
        ? Number(raw.ledger_transaction_id)
        : null,
    idempotency_key: raw.idempotency_key,
    status: raw.status,
    created_at: raw.created_at,
    updated_at: raw.updated_at,
  };
}

/**
 * Auth gate: allow users who can list OR add (backend may grant independently).
 */
export function ShopExpenseRoute() {
  const canAccess =
    hasPermission(PERMISSIONS.SHOP_EXPENSE_TABLE) ||
    hasPermission(PERMISSIONS.ADD_SHOP_EXPENSE);
  if (!canAccess) {
    return <Navigate to="/permissionrestricted" replace />;
  }
  return <ShopExpense />;
}

function ShopExpense() {
  const { buttonClass, tableClass, cardClass, buttonTableStyle } =
    useButtonTableStyle();
  const isCurrentFinancialYear = useIsCurrentFinancialYear();
  const socketRef = useRef(null);

  const [canList, setCanList] = useState(() =>
    hasPermission(PERMISSIONS.SHOP_EXPENSE_TABLE),
  );
  const [canAdd, setCanAdd] = useState(() =>
    hasPermission(PERMISSIONS.ADD_SHOP_EXPENSE),
  );
  const [canEdit, setCanEdit] = useState(() =>
    hasPermission(PERMISSIONS.EDIT_SHOP_EXPENSE),
  );

  useEffect(() => {
    const syncPermissions = () => {
      setCanList(hasPermission(PERMISSIONS.SHOP_EXPENSE_TABLE));
      setCanAdd(hasPermission(PERMISSIONS.ADD_SHOP_EXPENSE));
      setCanEdit(hasPermission(PERMISSIONS.EDIT_SHOP_EXPENSE));
    };

    window.addEventListener("permissionsUpdated", syncPermissions);
    window.addEventListener("erpAuthUpdated", syncPermissions);
    window.addEventListener("storage", syncPermissions);
    const interval = setInterval(syncPermissions, 1000);

    return () => {
      window.removeEventListener("permissionsUpdated", syncPermissions);
      window.removeEventListener("erpAuthUpdated", syncPermissions);
      window.removeEventListener("storage", syncPermissions);
      clearInterval(interval);
    };
  }, []);

  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    total_pages: 1,
  });
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState("add");
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);

  const [formType, setFormType] = useState(null);
  const [formAmount, setFormAmount] = useState("");
  const [formDate, setFormDate] = useState(
    () => new Date().toISOString().split("T")[0],
  );
  const [formComment, setFormComment] = useState("");
  const [formPayMode, setFormPayMode] = useState("bank_transfer");
  const [formBank, setFormBank] = useState(null);
  const [bankOptions, setBankOptions] = useState([]);
  const [loadingBanks, setLoadingBanks] = useState(false);

  const pageCache = useVisitedPageCache();

  const inFlightRef = useRef(false);

  const fetchList = useCallback(async (force = false) => {
    const cacheKey = visitedPageKey([currentPage, itemsPerPage]);
    if (force) pageCache.clear();
    const cached = force ? null : pageCache.read(cacheKey);
    if (cached && canList) {
      setRows(cached.items);
      setPagination(cached.pagination);
      setLoading(false);
      return;
    }
    if (!canList) {
      setRows([]);
      setPagination((p) => ({ ...p, total: 0, total_pages: 1 }));
      setLoading(false);
      return;
    }
    if (inFlightRef.current && !force) {
      return;
    }
    inFlightRef.current = true;
    setLoading(true);
    try {
      const res = await listShopExpenses({
        pagination: { page: currentPage, limit: itemsPerPage },
      });
      if (isErpApiSuccess(res) || res?.message === "radha") {
        const { rows: rawRows, pagination: pag } = normalizeListPayload(res);
        const mapped = rawRows.map(mapExpenseRow);
        const nextPagination = {
          page: pag.page,
          limit: pag.limit,
          total: pag.total,
          total_pages: pag.total_pages,
        };
        setRows(mapped);
        setPagination(nextPagination);
        pageCache.write(cacheKey, { items: mapped, pagination: nextPagination });
      } else {
        setRows([]);
        toast.error(res?.message || "Failed to load shop expenses");
      }
    } catch (err) {
      console.error(err);
      setRows([]);
      toast.error(
        getErpRequestErrorMessage(err, "Failed to load shop expenses"),
      );
    } finally {
      inFlightRef.current = false;
      setLoading(false);
    }
  }, [canList, currentPage, itemsPerPage]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  useEffect(() => {
    const onUrlChange = () => fetchList(true);
    window.addEventListener("apiUrlChanged", onUrlChange);
    return () => window.removeEventListener("apiUrlChanged", onUrlChange);
  }, [fetchList]);

  useEffect(() => {
    const socket = createErpSocket();
    socketRef.current = socket;

    const upsert = (expense) => {
      if (!expense?.id) return;
      const row = mapExpenseRow(expense);
      if (row.status === 0 || row.status === "0") {
        setRows((prev) => prev.filter((r) => r.id !== row.id));
        return;
      }
      setRows((prev) => {
        const idx = prev.findIndex((r) => r.id === row.id);
        if (idx >= 0) {
          const next = [...prev];
          next[idx] = row;
          return next;
        }
        if (prev.length < itemsPerPage && currentPage === 1) {
          return [row, ...prev].slice(0, itemsPerPage);
        }
        return prev;
      });
    };

    socket.on("shop_expense_created", (payload) => {
      if (payload?.expense) upsert(payload.expense);
    });
    socket.on("shop_expense_updated", (payload) => {
      if (payload?.expense) upsert(payload.expense);
    });
    socket.on("shop_expense_deleted", (payload) => {
      const id = payload?.id ?? payload?.expense?.id;
      if (id != null) {
        setRows((prev) => prev.filter((r) => r.id !== id));
      }
    });

    return () => {
      socket.off("shop_expense_created");
      socket.off("shop_expense_updated");
      socket.off("shop_expense_deleted");
      socket.disconnect();
      socketRef.current = null;
    };
  }, [currentPage, itemsPerPage]);

  const loadBankOptions = useCallback(async () => {
    setLoadingBanks(true);
    try {
      const branchId = getSessionBranchId();
      const res = await listBankAccounts(
        branchId ? { branch_id: branchId } : {},
      );
      const rows = Array.isArray(res?.data?.bank_accounts)
        ? res.data.bank_accounts
        : [];
      if (res?.success || res?.message === "radha") {
        const options = rows
          .filter(isMoneyBankAccount)
          .map(mapBankToOption)
          .sort((a, b) => b.value - a.value);
        setBankOptions(options);
        return options;
      }
      setBankOptions([]);
      return [];
    } catch (err) {
      console.error(err);
      setBankOptions([]);
      return [];
    } finally {
      setLoadingBanks(false);
    }
  }, []);

  const applyLastUsedBank = (options) => {
    if (!Array.isArray(options) || options.length === 0) return;
    try {
      const lastId = localStorage.getItem(LAST_BANK_STORAGE_KEY);
      if (!lastId) return;
      const match = options.find((o) => String(o.value) === String(lastId));
      if (match) setFormBank(match);
    } catch {
      /* ignore storage errors */
    }
  };

  useEffect(() => {
    if (!showModal) return;
    loadBankOptions();
  }, [showModal, loadBankOptions]);

  useEffect(() => {
    if (!showModal || modalMode !== "edit" || editingId == null) return;
    if (!needsBankAccount(formPayMode) || formBank) return;
    const row = rows.find((r) => r.id === editingId);
    if (!row?.bank_account_id) return;
    const opt = bankOptions.find((o) => o.value === row.bank_account_id);
    if (opt) {
      setFormBank(opt);
    } else if (row.bank_name) {
      setFormBank({
        value: row.bank_account_id,
        label: row.bank_name,
      });
    }
  }, [
    showModal,
    modalMode,
    editingId,
    formPayMode,
    formBank,
    rows,
    bankOptions,
  ]);

  useEffect(() => {
    if (!showModal || modalMode !== "add" || formBank) return;
    if (!needsBankAccount(formPayMode)) return;
    applyLastUsedBank(bankOptions);
  }, [showModal, modalMode, bankOptions, formBank, formPayMode]);

  const resetForm = () => {
    setFormType(null);
    setFormAmount("");
    setFormDate(new Date().toISOString().split("T")[0]);
    setFormComment("");
    setFormPayMode("bank_transfer");
    setFormBank(null);
    setEditingId(null);
    setModalMode("add");
  };

  const openAdd = () => {
    if (!canAdd) {
      toast.error("You do not have permission to add shop expenses.");
      return;
    }
    if (!isCurrentFinancialYear) {
      toast.error(FY_ADD_DISABLED_TITLE);
      return;
    }
    resetForm();
    setModalMode("add");
    setShowModal(true);
  };

  const openEdit = (row) => {
    if (!canEdit) return;
    setModalMode("edit");
    setEditingId(row.id);
    setFormType(
      SHOP_EXPENSE_TYPES.find((t) => t.value === row.expense_type) || null,
    );
    setFormAmount(String(row.amount ?? ""));
    setFormDate(row.expense_date || new Date().toISOString().split("T")[0]);
    setFormComment(row.comment || "");
    setFormPayMode(parseExpensePayMode(row));
    if (row.bank_account_id != null) {
      setFormBank({
        value: row.bank_account_id,
        label: row.bank_name || `Bank #${row.bank_account_id}`,
      });
    } else {
      setFormBank(null);
    }
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formType?.value) {
      toast.error("Please select expense type");
      return;
    }
    const amt = parseFloat(String(formAmount).replace(/,/g, ""));
    if (!Number.isFinite(amt) || amt <= 0) {
      toast.error("Enter a valid amount greater than zero");
      return;
    }
    const dateStr = String(formDate || "").trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      toast.error("Please select a valid expense date");
      return;
    }
    if (formType.value === "OTHER") {
      const c = String(formComment || "").trim();
      if (!c) {
        toast.error("Please add a description for “Other” expenses");
        return;
      }
    }
    if (needsBankAccount(formPayMode) && !formBank?.value) {
      toast.error("Please select a bank account for this payment type");
      return;
    }

    const branchId = getSessionBranchId();
    const paymentPayload = buildShopExpensePaymentPayload(
      formPayMode,
      formBank,
      branchId,
    );

    setSaving(true);
    try {
      if (modalMode === "edit" && editingId != null) {
        if (!canEdit) {
          toast.error("You do not have permission to edit expenses.");
          return;
        }
        const res = await updateShopExpense({
          id: editingId,
          expense_type: formType.value,
          amount: amt,
          expense_date: dateStr,
          comment:
            formType.value === "OTHER"
              ? String(formComment).trim()
              : formComment
                ? String(formComment).trim()
                : null,
          ...paymentPayload,
        });
        if (isErpApiSuccess(res) || res?.message === "radha") {
          toast.success(
            res?.message && res.message !== "radha"
              ? res.message
              : "Expense updated",
          );
          setShowModal(false);
          resetForm();
          await fetchList(true);
        } else {
          toast.error(res?.message || "Could not update expense");
        }
      } else {
        if (!canAdd) {
          toast.error("You do not have permission to add expenses.");
          return;
        }
        const idempotencyKey = generateIdempotencyKey();
        const note = String(formComment || "").trim();
        const res = await createShopExpense(
          {
            expense_type: formType.value,
            amount: amt,
            expense_date: dateStr,
            comment: formType.value === "OTHER" ? note : note ? note : null,
            ...paymentPayload,
          },
          { idempotencyKey },
        );
        if (isErpApiSuccess(res) || res?.message === "radha") {
          if (needsBankAccount(formPayMode) && formBank?.value) {
            try {
              localStorage.setItem(
                LAST_BANK_STORAGE_KEY,
                String(formBank.value),
              );
            } catch {
              /* ignore */
            }
          }
          if (res?.data?.idempotent_replay) {
            toast.success("Request already processed (idempotent replay).");
          } else {
            toast.success(
              res?.message && res.message !== "radha"
                ? res.message
                : "Expense saved",
            );
          }
          setShowModal(false);
          resetForm();
          await fetchList(true);
        } else {
          toast.error(res?.message || "Could not save expense");
        }
      }
    } catch (err) {
      console.error(err);
      toast.error(
        getErpRequestErrorMessage(
          err,
          modalMode === "edit"
            ? "Could not update expense"
            : "Could not save expense",
        ),
      );
    } finally {
      setSaving(false);
    }
  };

  const closeDeleteModal = () => {
    if (!deleteSubmitting) setDeleteTarget(null);
  };

  const confirmDeleteExpense = async () => {
    if (!deleteTarget || !canEdit) return;
    setDeleteSubmitting(true);
    try {
      const res = await deleteShopExpense({ id: deleteTarget.id });
      if (isErpApiSuccess(res) || res?.message === "radha") {
        toast.success(
          res?.message && res.message !== "radha"
            ? res.message
            : "Expense removed",
        );
        setDeleteTarget(null);
        await fetchList(true);
      } else {
        toast.error(res?.message || "Could not delete expense");
      }
    } catch (err) {
      toast.error(getErpRequestErrorMessage(err, "Could not delete expense"));
    } finally {
      setDeleteSubmitting(false);
    }
  };

  const selectStyles = {
    control: (p) => ({
      ...p,
      minHeight: 44,
      borderRadius: buttonTableStyle === "square" ? 0 : 8,
      borderColor: "#e0e6eb",
      boxShadow: "none",
    }),
    menu: (p) => ({ ...p, zIndex: 9999 }),
  };

  const formatDisplayDate = (val) => {
    if (!val) return "—";
    const s = String(val).split("T")[0];
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
      try {
        return formatDateToDDMMYYYY(s);
      } catch {
        return s;
      }
    }
    return val;
  };

  const tableColSpan = canEdit ? 6 : 5;

  return (
    <div className="body-wrapper">
      <Toaster position="top-center" />
      <div className="container-fluid">
        <div
          className={`card w-100 position-relative overflow-hidden ${cardClass} ${
            buttonTableStyle === "square" ? "rounded-0" : ""
          }`}
        >
          <div className="px-4 py-3 border-bottom d-flex justify-content-between align-items-center flex-wrap gap-2">
            <div>
              <h4 className="card-title mb-0">Shop Expense Table</h4>
            </div>
            {canAdd && (
              <button
                type="button"
                className={`btn btn-success ${buttonClass}`}
                onClick={openAdd}
                disabled={!isCurrentFinancialYear}
                title={
                  !isCurrentFinancialYear
                    ? FY_ADD_DISABLED_TITLE
                    : "Add shop expense"
                }
              >
                Add Shop Expense
              </button>
            )}
          </div>

          <div className="card-body p-4">
            {!canList && canAdd && (
              <div className="alert alert-info mb-3 py-2 small">
                You can add expenses. Viewing the list requires{" "}
                <strong>Shop expense (table)</strong> permission.
              </div>
            )}

            {!canList ? (
              <div className="text-center text-muted py-4">
                No list access. Use <strong>Add Shop Expense</strong> to create
                an entry.
              </div>
            ) : (
              <>
                <div
                  className={`table-responsive mb-4 border ${cardClass} ${
                    buttonTableStyle === "square" ? "rounded-0" : ""
                  }`}
                >
                  <table
                    className={`table text-nowrap mb-0 align-middle ${tableClass} ${
                      buttonTableStyle === "square" ? "rounded-0" : ""
                    }`}
                  >
                    <thead className="text-dark fs-4">
                      <tr>
                        <th>Expense date</th>
                        <th>Type</th>
                        <th>Paid via</th>
                        <th className="text-end">Amount</th>
                        <th
                          className="text-wrap"
                          style={{ minWidth: "18rem", width: "36%" }}
                        >
                          Comment
                        </th>
                        {canEdit && <th>Actions</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td
                            colSpan={tableColSpan}
                            className="text-center py-4"
                          >
                            Loading...
                          </td>
                        </tr>
                      ) : rows.length === 0 ? (
                        <tr>
                          <td
                            colSpan={tableColSpan}
                            className="text-center py-5 text-muted"
                          >
                            No expenses found.
                          </td>
                        </tr>
                      ) : (
                        rows.map((r) => (
                          <tr key={r.id}>
                            <td>{formatDisplayDate(r.expense_date)}</td>
                            <td>
                              <span
                                className={`badge ${badgeClassForExpenseType(r.expense_type)}`}
                                style={{ fontSize: "0.85rem" }}
                              >
                                {labelForType(r.expense_type)}
                              </span>
                            </td>
                            <td>
                              <span className="fw-medium">
                                {formatPaymentSource(r)}
                              </span>
                            </td>
                            <td className="text-end text-danger fw-semibold">
                              ₹
                              {r.amount.toLocaleString("en-IN", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                            </td>
                            <td
                              className="text-wrap"
                              style={{ minWidth: "18rem", width: "36%" }}
                            >
                              {r.comment || "—"}
                            </td>
                            {canEdit && (
                              <td>
                                <button
                                  type="button"
                                  title="Edit"
                                  className={`btn btn-primary btn-sm me-1 ${buttonClass}`}
                                  onClick={() => openEdit(r)}
                                >
                                  <Icon
                                    icon="solar:pen-bold"
                                    width="16"
                                    height="16"
                                  />
                                </button>
                                <button
                                  type="button"
                                  title="Delete"
                                  className={`btn btn-outline-danger btn-sm ${buttonClass}`}
                                  onClick={() => setDeleteTarget(r)}
                                >
                                  <Icon
                                    icon="solar:trash-bin-trash-bold"
                                    width="16"
                                    height="16"
                                  />
                                </button>
                              </td>
                            )}
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
                {!loading && pagination.total > 0 && (
                  <Pagination
                    totalItems={pagination.total}
                    itemsPerPage={itemsPerPage}
                    currentPage={currentPage}
                    onPageChange={setCurrentPage}
                    onItemsPerPageChange={(n) => {
                      setItemsPerPage(n);
                      setCurrentPage(1);
                    }}
                  />
                )}
              </>
            )}
          </div>
        </div>
      </div>

      <Modal
        show={showModal}
        onHide={() => !saving && setShowModal(false)}
        centered
        backdrop="static"
        size="md"
        contentClassName={`${cardClass} ${
          buttonTableStyle === "square" ? "rounded-0" : ""
        }`}
      >
        <Modal.Header closeButton={!saving}>
          <Modal.Title>
            {modalMode === "edit" ? "Edit Shop Expense" : "Add Shop Expense"}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            {modalMode === "edit" && editingId != null && (() => {
              const detail = rows.find((r) => r.id === editingId);
              if (!detail) return null;
              return (
                <div className="border rounded p-3 mb-3 bg-light bg-opacity-50 small">
                  <div className="fw-semibold mb-2">Payment details</div>
                  <div className="d-flex justify-content-between gap-2 mb-1">
                    <span className="text-muted">Payment type</span>
                    <span>{labelForPayMode(parseExpensePayMode(detail))}</span>
                  </div>
                  {detail.bank_account_id != null && (
                    <div className="d-flex justify-content-between gap-2 mb-1">
                      <span className="text-muted">Bank</span>
                      <span>{detail.bank_name || `ID ${detail.bank_account_id}`}</span>
                    </div>
                  )}
                  {detail.document_id != null && (
                    <div className="d-flex justify-content-between gap-2 mb-1">
                      <span className="text-muted">Document ID</span>
                      <span>{detail.document_id}</span>
                    </div>
                  )}
                  {detail.ledger_transaction_id != null && (
                    <div className="d-flex justify-content-between gap-2">
                      <span className="text-muted">Ledger transaction</span>
                      <span>{detail.ledger_transaction_id}</span>
                    </div>
                  )}
                </div>
              );
            })()}
            <Form.Group className="mb-3">
              <Form.Label>Type</Form.Label>
              <Select
                inputId="shop-expense-type"
                options={SHOP_EXPENSE_TYPES}
                value={formType}
                onChange={(opt) => {
                  setFormType(opt);
                }}
                styles={selectStyles}
                placeholder="Select type…"
                isClearable={modalMode === "add"}
                isDisabled={modalMode === "edit" && saving}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Amount</Form.Label>
              <Form.Control
                type="number"
                step="0.01"
                min="0.01"
                className={buttonClass}
                value={formAmount}
                onChange={(e) => setFormAmount(e.target.value)}
                placeholder="0.00"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Expense date</Form.Label>
              <Form.Control
                type="date"
                className={buttonClass}
                value={formDate}
                onChange={(e) => setFormDate(e.target.value)}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Payment type</Form.Label>
              <Select
                inputId="shop-expense-payment-type"
                options={SHOP_EXPENSE_PAYMENT_OPTIONS}
                value={
                  SHOP_EXPENSE_PAYMENT_OPTIONS.find(
                    (o) => o.value === formPayMode,
                  ) || null
                }
                onChange={(opt) => {
                  const next = opt?.value || "bank_transfer";
                  setFormPayMode(next);
                  if (!needsBankAccount(next)) {
                    setFormBank(null);
                  } else {
                    applyLastUsedBank(bankOptions);
                  }
                }}
                styles={selectStyles}
                placeholder="Select payment type…"
                isDisabled={saving}
              />
            </Form.Group>
            {needsBankAccount(formPayMode) && (
              <Form.Group className="mb-3">
                <Form.Label>
                  Bank account 
                </Form.Label>
                <Select
                  inputId="shop-expense-bank"
                  options={bankOptions}
                  value={formBank}
                  onChange={setFormBank}
                  styles={selectStyles}
                  placeholder={
                    loadingBanks ? "Loading banks…" : "Select bank account…"
                  }
                  isLoading={loadingBanks}
                  isDisabled={saving}
                  isClearable
                />
              </Form.Group>
            )}
            {formType && (
              <Form.Group className="mb-2">
                <Form.Label>
                  Comment
                  {formType.value === "OTHER" && (
                    <span className="text-danger"> *</span>
                  )}
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  className={buttonClass}
                  value={formComment}
                  onChange={(e) => setFormComment(e.target.value)}
                  placeholder={
                    formType.value === "OTHER"
                      ? "Required for “Other” — e.g. courier, repairs, misc.…"
                      : "Optional note (required when type is Other)"
                  }
                />
              </Form.Group>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="primary"
              className={buttonClass}
              type="submit"
              disabled={saving}
            >
              {saving ? "Saving…" : modalMode === "edit" ? "Update" : "Save"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal
        show={deleteTarget != null}
        onHide={closeDeleteModal}
        centered
        backdrop={deleteSubmitting ? "static" : true}
        keyboard={!deleteSubmitting}
        contentClassName={`${cardClass} ${
          buttonTableStyle === "square" ? "rounded-0" : ""
        }`}
      >
        <Modal.Header closeButton={!deleteSubmitting}>
          <Modal.Title>Delete shop expense</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {deleteTarget && (
            <div className="border rounded p-3 bg-light bg-opacity-50 small">
              <div className="d-flex justify-content-between gap-2 mb-1">
                <span className="text-muted">Type</span>
                <span className="fw-medium">
                  {labelForType(deleteTarget.expense_type)}
                </span>
              </div>
              <div className="d-flex justify-content-between gap-2 mb-1">
                <span className="text-muted">Amount</span>
                <span className="fw-semibold text-danger">
                  ₹
                  {deleteTarget.amount.toLocaleString("en-IN", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </span>
              </div>
              <div className="d-flex justify-content-between gap-2 mb-1">
                <span className="text-muted">Paid via</span>
                <span>{formatPaymentSource(deleteTarget)}</span>
              </div>
              <div className="d-flex justify-content-between gap-2">
                <span className="text-muted">Expense date</span>
                <span>{formatDisplayDate(deleteTarget.expense_date)}</span>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="danger"
            className={buttonClass}
            onClick={confirmDeleteExpense}
            disabled={deleteSubmitting}
          >
            {deleteSubmitting ? "Deleting…" : "Delete"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default ShopExpense;
